import { useState, forwardRef } from 'react'
import { useQuery } from 'react-query'
import {
  ChartBarIcon,
  XCircleIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline'
import DatePicker from 'react-datepicker'

import 'react-datepicker/dist/react-datepicker.css'
import { query } from '../api'

const FUNNEL_EVENT_NAMES = [
  'CREATE_CAREER_STEP_1_VIEWED',
  'CREATE_CAREER_STEP_2_VIEWED',
  'CREATE_CAREER_STEP_3_VIEWED',
  'CREATE_CAREER_STEP_4_VIEWED',
  'CREATE_CAREER_STEP_5_VIEWED',
  'FINISH_CREATE_CAREER',
  'ABORT_CREATE_CAREER'
]

const FUNNEL_STEPS = [
  {
    key: 'CREATE_CAREER_STEP_1_VIEWED',
    label: 'Einstieg in den Flow',
    step: 1
  },
  { key: 'CREATE_CAREER_STEP_2_VIEWED', label: 'Kulturprofil', step: 2 },
  {
    key: 'CREATE_CAREER_STEP_3_VIEWED',
    label: 'Anforderungsprofil',
    step: 3,
    critical: false
  },
  { key: 'CREATE_CAREER_STEP_4_VIEWED', label: 'Testbatterie', step: 4 },
  { key: 'CREATE_CAREER_STEP_5_VIEWED', label: 'Übersicht', step: 5 },
  { key: 'FINISH_CREATE_CAREER', label: 'Stelle aktiviert', step: 6 }
]

const FUNNEL_LIMIT = 500

const FUNNEL_START_DATE = new Date('2026-03-19')

const DATE_PRESETS = [
  { label: 'Alles', days: null },
  { label: '7 Tage', days: 7 },
  { label: '30 Tage', days: 30 },
  { label: '3 Monate', days: 90 }
]

const getPresetDate = (days) => {
  if (!days) return new Date(FUNNEL_START_DATE)
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d < FUNNEL_START_DATE ? new Date(FUNNEL_START_DATE) : d
}

const toUtcMidnightIso = (date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}T00:00:00Z`
}

const DatePickerTrigger = forwardRef(({ value, onClick }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    className='flex items-center gap-1.5 rounded-lg bg-paleWhiteBlue px-2.5 py-1 text-xs font-medium text-primaryBlue hover:bg-lightBlue transition-colors focus:outline-none focus:ring-2 focus:ring-primaryBlue focus:ring-offset-1'
  >
    <CalendarDaysIcon className='h-3.5 w-3.5' />
    ab {value}
  </button>
))

const parseAnalyticsResponse = (value) => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

const computeFunnel = (records) => {
  // Build sets of user_ids per event
  const usersByEvent = {}
  for (const record of records) {
    if (!usersByEvent[record.event_name]) {
      usersByEvent[record.event_name] = new Set()
    }
    usersByEvent[record.event_name].add(record.user_id)
  }

  // Cohort = users who started at Step 1
  const cohort = usersByEvent['CREATE_CAREER_STEP_1_VIEWED'] || new Set()
  const cohortSize = cohort.size

  // Count only cohort users per step + track mid-funnel entries
  const steps = FUNNEL_STEPS.map((step) => {
    const allUsersAtStep = usersByEvent[step.key] || new Set()
    const cohortCount = [...allUsersAtStep].filter((uid) =>
      cohort.has(uid)
    ).length
    const midFunnelCount = allUsersAtStep.size - cohortCount
    return {
      ...step,
      count: cohortCount,
      totalCount: allUsersAtStep.size,
      midFunnelCount
    }
  })

  // Unique users across all steps who never triggered Step 1
  const allFunnelUsers = new Set()
  for (const s of FUNNEL_STEPS) {
    const users = usersByEvent[s.key] || new Set()
    for (const uid of users) allFunnelUsers.add(uid)
  }
  const midFunnelEntries = [...allFunnelUsers].filter(
    (uid) => !cohort.has(uid)
  ).length

  const enrichedSteps = steps.map((step, i) => {
    if (i === 0) {
      return {
        ...step,
        dropoffRate: 0,
        droppedUsers: 0,
        conversionRate: 100,
        barWidth: 100
      }
    }
    const prev = steps[i - 1]
    const dropped = Math.max(0, prev.count - step.count)
    const dropoffRate = prev.count > 0 ? (dropped / prev.count) * 100 : 0
    const conversionRate = cohortSize > 0 ? (step.count / cohortSize) * 100 : 0
    const barWidth = cohortSize > 0 ? (step.count / cohortSize) * 100 : 0
    return {
      ...step,
      dropoffRate,
      droppedUsers: dropped,
      conversionRate,
      barWidth
    }
  })

  return { steps: enrichedSteps, cohortSize, midFunnelEntries }
}

const getConversionColor = (rate) => {
  if (rate >= 55) return 'text-primaryGreen'
  if (rate >= 35) return 'text-mango'
  if (rate >= 15) return 'text-safetyOrange'
  return 'text-dislikeRed'
}

const getDropoffColor = (rate, isMaxDropoff) => {
  if (isMaxDropoff)
    return {
      bar: 'bg-dislikeRed',
      badge: 'bg-[#FFF0EE] text-dislikeRed ring-[#FFCCC8]',
      arrow: 'text-dislikeRed'
    }
  if (rate >= 30)
    return {
      bar: 'bg-safetyOrange',
      badge: 'bg-[#FFF4EC] text-safetyOrange ring-[#FFD9BA]',
      arrow: 'text-safetyOrange'
    }
  if (rate >= 15)
    return {
      bar: 'bg-mango',
      badge: 'bg-[#FFFAED] text-[#A06800] ring-[#FFE4A0]',
      arrow: 'text-mango'
    }
  return {
    bar: 'bg-brightBlue',
    badge: 'bg-paleWhiteBlue text-primaryBlue ring-lightBlue',
    arrow: 'text-softGrey'
  }
}

const FunnelChart = ({ steps, abortCount }) => {
  const maxDropoff = steps
    .slice(1)
    .reduce(
      (max, step) =>
        step.dropoffRate > max.rate
          ? { rate: step.dropoffRate, key: step.key }
          : max,
      { rate: 0, key: null }
    )

  return (
    <div className='space-y-1'>
      {steps.map((step, i) => {
        const isMaxDropoff = step.key === maxDropoff.key
        const colors = getDropoffColor(step.dropoffRate, isMaxDropoff)
        const isCritical = step.critical

        return (
          <div key={step.key}>
            {/* Drop-off connector */}
            {i > 0 && step.dropoffRate > 0 && (
              <div
                className={`flex items-center gap-2 py-1 pl-4 ${isMaxDropoff ? 'bg-[#FFF0EE] rounded-lg' : ''}`}
              >
                <svg
                  className={`h-4 w-4 shrink-0 ${colors.arrow}`}
                  viewBox='0 0 16 16'
                  fill='currentColor'
                >
                  <path d='M8 3a.5.5 0 0 1 .5.5v7.793l2.146-2.147a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 .708-.708L7.5 11.293V3.5A.5.5 0 0 1 8 3z' />
                </svg>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${colors.badge}`}
                >
                  {isMaxDropoff && '⚠ '}−{step.dropoffRate.toFixed(1)}%
                  Drop-off
                  {isMaxDropoff && ' · höchster Verlust'}
                </span>
                <span className='text-xs text-mediumGrey'>
                  {step.droppedUsers} User abgesprungen
                </span>
              </div>
            )}

            {/* Step bar */}
            <div
              className={`rounded-xl p-3 ${isCritical ? 'ring-2 ring-safetyOrange bg-[#FFF8F2]' : 'bg-white'}`}
            >
              <div className='flex items-center gap-3 mb-2'>
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                    step.step === 6
                      ? 'bg-[#E2F9F8] text-primaryGreen'
                      : 'bg-paleWhiteBlue text-primaryBlue'
                  }`}
                >
                  {step.step}
                </span>
                <span className='text-sm font-medium text-darkBlack flex-1'>
                  {step.label}
                  {isCritical && (
                    <span className='ml-2 inline-flex items-center rounded-full bg-[#FFF4EC] px-2 py-0.5 text-xs font-medium text-safetyOrange'>
                      Kritischer Schritt
                    </span>
                  )}
                </span>
                <div className='flex items-center gap-3'>
                  <span className='text-lg font-bold text-darkBlack'>
                    {step.count}
                  </span>
                  <span className='text-xs text-mediumGrey w-12 text-right'>
                    {i === 0 ? '100%' : `${step.conversionRate.toFixed(1)}%`}
                  </span>
                </div>
              </div>
              <div className='h-3 w-full rounded-full bg-lightGrey overflow-hidden'>
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    step.step === 6
                      ? 'bg-primaryGreen'
                      : isMaxDropoff
                        ? 'bg-dislikeRed'
                        : isCritical
                          ? 'bg-safetyOrange'
                          : 'bg-brightBlue'
                  }`}
                  style={{ width: `${step.barWidth}%` }}
                />
              </div>
            </div>
          </div>
        )
      })}

      {/* Abort metric */}
      {abortCount > 0 && (
        <div className='mt-4 flex items-center gap-3 rounded-xl border border-dashed border-[#FFCCC8] bg-[#FFF0EE] p-3'>
          <XCircleIcon className='h-5 w-5 text-dislikeRed shrink-0' />
          <div className='flex-1'>
            <p className='text-sm font-medium text-dislikeRed'>
              Flow abgebrochen
            </p>
            <p className='text-xs text-[#FF9685]'>
              Explizit über ABORT_CREATE_CAREER
            </p>
          </div>
          <span className='text-lg font-bold text-dislikeRed'>
            {abortCount}
          </span>
        </div>
      )}
    </div>
  )
}

const Analytics = () => {
  const [fromDate, setFromDate] = useState(FUNNEL_START_DATE)
  const [selectedPreset, setSelectedPreset] = useState('Alles')

  const fromTs = toUtcMidnightIso(fromDate)

  const {
    data: funnelData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['analytics-funnel', fromTs],
    queryFn: () =>
      query({
        query: 'listAnalyticsEvents',
        variables: {
          filter: JSON.stringify({
            event_names: FUNNEL_EVENT_NAMES,
            from_ts: fromTs,
            limit: FUNNEL_LIMIT
          })
        }
      })
  })

  const parsedData = funnelData ? parseAnalyticsResponse(funnelData) : null
  const records = parsedData?.records ?? []
  const funnelResult = records.length > 0 ? computeFunnel(records) : null

  const abortUsers =
    records.length > 0
      ? new Set(
          records
            .filter((r) => r.event_name === 'ABORT_CREATE_CAREER')
            .map((r) => r.user_id)
        ).size
      : 0

  return (
    <div>
      <div className='sm:flex sm:items-center'>
        <div className='sm:flex-auto'>
          <div className='flex items-center gap-3'>
            <span className='inline-flex rounded-lg bg-paleWhiteBlue p-2 text-primaryBlue'>
              <ChartBarIcon className='h-6 w-6' />
            </span>
            <h1 className='text-base font-semibold leading-6 text-darkBlack'>
              Analytics
            </h1>
          </div>
          <p className='mt-2 text-sm text-rockGrey'>
            Funnel-Tracking, Conversion-Rates und Drop-off-Analysen.
          </p>
        </div>
      </div>

      <div className='mt-6'>
        {isLoading && (
          <p className='text-sm text-mediumGrey'>Lade Funnel-Daten...</p>
        )}
        {error && (
          <pre className='text-sm text-dislikeRed whitespace-pre-wrap'>
            {JSON.stringify(error, null, 2)}
          </pre>
        )}

        {!isLoading && !error && records.length === 0 && (
          <div className='rounded-2xl bg-backgroundGrey p-6 border border-softGrey flex flex-col items-center justify-center gap-2 py-16 text-center'>
            <ChartBarIcon className='h-8 w-8 text-softGrey' />
            <p className='text-sm font-medium text-darkBlack'>
              Noch keine Daten für diesen Zeitraum
            </p>
            <p className='text-xs text-mediumGrey'>
              Wähle einen anderen Zeitraum oder warte auf die ersten Events.
            </p>
          </div>
        )}

        {funnelResult && (
          <div className='rounded-2xl bg-backgroundGrey p-6 border border-softGrey'>
            <div className='mb-5 flex items-center justify-between'>
              <div>
                <h2 className='text-sm font-semibold text-darkBlack'>
                  Create Career – Funnel
                </h2>
                <div className='mt-1 flex items-center gap-2'>
                  <p className='text-xs text-mediumGrey'>
                    Kohorte ab Schritt 1 ·
                  </p>
                  <DatePicker
                    selected={fromDate}
                    onChange={(date) => {
                      if (date) {
                        setFromDate(date)
                        setSelectedPreset(null)
                      }
                    }}
                    minDate={FUNNEL_START_DATE}
                    maxDate={new Date()}
                    dateFormat='dd.MM.yyyy'
                    customInput={<DatePickerTrigger />}
                    popperPlacement='bottom-start'
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode='select'
                  />
                </div>
                <div className='mt-1.5 flex items-center gap-1.5'>
                  {DATE_PRESETS.map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => {
                        setFromDate(getPresetDate(preset.days))
                        setSelectedPreset(preset.label)
                      }}
                      className={`rounded-md px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primaryBlue focus:ring-offset-1 ${
                        selectedPreset === preset.label
                          ? 'bg-primaryBlue text-white'
                          : 'bg-paleWhiteBlue text-primaryBlue hover:bg-lightBlue'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className='text-right'>
                <p className='text-xs text-mediumGrey'>Gesamt-Conversion</p>
                <p
                  className={`text-xl font-bold ${
                    funnelResult.cohortSize > 0
                      ? getConversionColor(
                          funnelResult.steps[funnelResult.steps.length - 1]
                            .conversionRate
                        )
                      : 'text-mediumGrey'
                  }`}
                >
                  {funnelResult.cohortSize > 0
                    ? `${funnelResult.steps[funnelResult.steps.length - 1].conversionRate.toFixed(1)}%`
                    : '—'}
                </p>
              </div>
            </div>
            {funnelResult.midFunnelEntries > 0 && (
              <div className='mb-4 flex items-start gap-2 rounded-lg border border-lightBlue bg-paleWhiteBlue px-3 py-2 text-xs text-primaryBlue'>
                <span className='mt-px shrink-0'>ⓘ</span>
                <span>
                  {funnelResult.midFunnelEntries} User haben den Flow ohne
                  Schritt 1 betreten und werden im Funnel nicht gezählt. Alle
                  Werte basieren auf der Kohorte ab Schritt 1 (
                  {funnelResult.cohortSize} User).
                </span>
              </div>
            )}
            <FunnelChart steps={funnelResult.steps} abortCount={abortUsers} />
          </div>
        )}
      </div>
    </div>
  )
}

export default Analytics
